import { useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { camelizeKeys } from 'humps';
import { useTranslation } from 'react-i18next';
import { fetchConfiguration } from 'store/actions/booking';

const defaultConfiguration = {
  afternoonTiming: { isEnabled: true, isRequired: false, isConfigurable: true, section: 'appointment_details' },
  availabilityPreferencesDetail: { isEnabled: true, isRequired: false, isConfigurable: true, section: 'appointment_details' },
  availabilitySelector: { isEnabled: true, isRequired: false, isConfigurable: true, section: 'appointment_details' },
  birthDate: { isEnabled: true, isRequired: true, isConfigurable: false, section: 'client_information' },
  civilStatus: { isEnabled: true, isRequired: false, isConfigurable: true, section: 'client_information' },
  email: { isEnabled: true, isRequired: true, isConfigurable: false, section: 'client_information' },
  employmentStatus: { isEnabled: true, isRequired: false, isConfigurable: true, section: 'client_information' },
  eveningTiming: { isEnabled: true, isRequired: false, isConfigurable: true, section: 'appointment_details' },
  feeType: { isEnabled: true, isRequired: false, isConfigurable: true, section: 'fees' },
  firstName: { isEnabled: true, isRequired: true, isConfigurable: false, section: 'client_information' },
  formattedAddress: { isEnabled: true, isRequired: true, isConfigurable: false, section: 'client_information' },
  healthcareServiceId: { isEnabled: true, isRequired: false, isConfigurable: true, section: 'service_details' },
  homeNumber: { isEnabled: true, isRequired: false, isConfigurable: true, section: 'client_information' },
  isPatientInsured: { isEnabled: true, isRequired: false, isConfigurable: true, section: 'fees' },
  lastName: { isEnabled: true, isRequired: true, isConfigurable: false, section: 'client_information' },
  locationPreference: { isEnabled: true, isRequired: false, isConfigurable: true, section: 'appointment_details' },
  mobileNumber: { isEnabled: true, isRequired: false, isConfigurable: true, section: 'client_information' },
  morningTiming: { isEnabled: true, isRequired: false, isConfigurable: true, section: 'appointment_details' },
  preferredProviderGender: { isEnabled: true, isRequired: false, isConfigurable: true, section: 'service_details' },
  presentingIssueIds: { isEnabled: true, isRequired: false, isConfigurable: true, section: 'service_details' },
  serviceLanguages: { isEnabled: true, isRequired: false, isConfigurable: true, section: 'service_details' },
  timezone: { isEnabled: true, isRequired: true, isConfigurable: false, section: 'appointment_details' },
  payments: { isEnabled: false, isRequired: true, isConfigurable: false, section: 'payments' },
  summary: { isEnabled: true, isRequired: false, isConfigurable: false, section: 'summary', translation: '' },
};

export default () => {
  const { i18n } = useTranslation('labels');
  const { data, loading } = useSelector((state) => state.booking);
  const dispatch = useDispatch();
  useEffect(() => {
    if (!data) dispatch(fetchConfiguration());
  }, []);

  const configuration = useMemo(() => {
    if (data) {
      const cf = data || [];
      const conf = cf.reduce((acc, value) => {
        acc[value.code] = {
          isEnabled: value.isEnabled,
          isRequired: value.isRequired,
          isConfigurable: value.isConfigurable,
          section: value.section,
          translation: value.translations[i18n.language].body,
        };
        return acc;
      }, {});
      return camelizeKeys(conf);
    }
    return defaultConfiguration;
  }, [data, i18n.language]);

  const isRequiredField = useCallback((name) => configuration[name]?.isRequired ?? false, [configuration]);
  const isEnabledField = useCallback((name) => configuration[name]?.isEnabled ?? true, [configuration]);
  const isEnabledSection = useCallback((section) => Object.values(configuration).some((x) => x.isEnabled && x.section === section), [configuration]);
  return { data: configuration, isLoading: loading, isRequiredField, isEnabledField, isEnabledSection };
};
